import axios, { AxiosResponse } from "axios";
import _ from "lodash";

import { isThomasnetDomain } from "./routerUtils";
import { Origins } from "../components/proposals/contexts/configContext";

import {
  MarketplaceProposal,
  PfsFileData,
  UnitOfMeasurement,
  PfsFile,
} from "../schema/generated/schema";
import { getAuthHeadersFromCookie } from "./generalUtils";

export enum FileShareEntityType {
  PROPOSAL = "PROPOSAL",
  OPPORTUNITY = "OPPORTUNITY",
  MARKETPLACE_ORDER = "MARKETPLACE_ORDER",
  INVOICE = "INVOICE",
  FORM = "FORM",
}

export type FileEntity = {
  type: FileShareEntityType;
  id: string;
};

const uploadToUrl = async (uploadUrl: string, file: File): Promise<PfsFile> => {
  const data = new FormData();
  data.append("file", file);
  const pfsData = await axios.put<any, AxiosResponse<PfsFile>>(uploadUrl, data);
  return pfsData.data;
};

const pfsUploadFiles = async (
  partFilesServiceOrigin: string,
  files: File[],
  userId: string,
  shareContext:
    | undefined
    | { entityType: string; entityId: string; users: any[] } = undefined,
  fileUnits: Record<string, UnitOfMeasurement> = {},
  authHeaders: any
): Promise<PfsFile[]> => {
  const fileUploads = files.map(async (file: File) => {
    const data = new FormData();
    const geometry = fileUnits[file.name]
      ? { unitOfMeasurement: fileUnits[file.name] }
      : {};
    const context = shareContext
      ? {
          SHARED_ON_ENTITY: {
            name: file.name,
            entityType: shareContext.entityType,
            entityId: shareContext.entityId,
            users: shareContext.users,
            geometry,
          },
        }
      : {
          CUSTOMER_UPLOAD: {
            name: file.name,
            geometry,
          },
        };
    data.append(
      "json",
      JSON.stringify({
        context,
        owner: {
          type: "XOMETRY",
          externalId: userId,
        },
      })
    );
    data.append("file", file);
    const pfsData = await axios.put<any, AxiosResponse<PfsFile>>(
      `${partFilesServiceOrigin}/file`,
      data,
      {
        headers: _.isEmpty(authHeaders)
          ? getAuthHeadersFromCookie()
          : authHeaders,
        withCredentials: true,
      }
    );
    return pfsData;
  });
  const responses = await Promise.all(fileUploads);
  return responses.map(response => response.data);
};

const getFileThumbnailUrl = async (
  partFilesServiceOrigin: string,
  fileId: string
): Promise<string> => {
  const thumbnailData = await axios.get<string>(
    `${partFilesServiceOrigin}/thumbnail/${fileId}`,
    {
      headers: getAuthHeadersFromCookie(),
      withCredentials: true,
    }
  );
  return thumbnailData.data;
};

const getFileStreamcacheUrl = async (
  partFilesServiceOrigin: string,
  fileId: string
): Promise<string> => {
  const streamcacheData = await axios.get<string>(
    `${partFilesServiceOrigin}/streamcache/${fileId}`,
    {
      headers: getAuthHeadersFromCookie(),
      withCredentials: true,
    }
  );
  return streamcacheData.data;
};

const wait = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const callWithRetry = async (fn, depth = 0) => {
  try {
    return await fn();
  } catch (e) {
    if (depth > 20) {
      throw e;
    }
    // await wait(2 ** (depth + 5) * 10); // Exponential back off
    await wait(10000); // Linear back off

    return callWithRetry(fn, depth + 1);
  }
};

// Taken from https://github.com/xometry/pricing-everywhere/blob/master/src/lib/features/part_create.py#L23
const GEOMETRY_SUPPORTED_FILE_EXTENSIONS = [
  "3dxml",
  "3mf",
  "catpart",
  "dxf",
  "ipt",
  "prt",
  "sat",
  "sldprt",
  "step",
  "stl",
  "stp",
  "x_b",
  "x_t",
];

export const getFileExtension = (
  file?: ModelViewerFile | string
): string | undefined => {
  const filename =
    typeof file === "string" ? file : file?.data?.originalFilename;
  const fileNameParts = filename?.split(".") || [];
  return fileNameParts.length >= 2 ? fileNameParts.pop() : undefined;
};

const shouldHaveStreamcacheFile = (file: ModelViewerFile | string) => {
  const fileExt = getFileExtension(file);
  return (
    !!fileExt &&
    GEOMETRY_SUPPORTED_FILE_EXTENSIONS.includes(fileExt.toLowerCase())
  );
};

const shouldHaveThumbnailImage = (file: ModelViewerFile | string) => {
  return shouldHaveStreamcacheFile(file);
};

const UNITS_NEED_USER_INPUT_FILE_EXTENSIONS = ["stl"];

const fileNeedUserInputForUnits = (file?: ModelViewerFile | string) => {
  const fileExtension = getFileExtension(file);
  return (
    !!fileExtension &&
    UNITS_NEED_USER_INPUT_FILE_EXTENSIONS.includes(fileExtension.toLowerCase())
  );
};

const getOriginalFilename = (file: ModelViewerFile) => {
  return _.get(file, "data.originalFilename", "untitled");
};

const buildDownloadUrl = (origins: Origins, file: PfsFile) => {
  const rpcDomain = isThomasnetDomain() ? origins.thomasnetXApi : origins.www;
  const originalFilename = getOriginalFilename(file);
  const { entityType, entityId } = file.contextMeta;
  return `${rpcDomain}/rpc/marketplace-file/${entityType}/${entityId}/${file.id}/${originalFilename}`;
};

const getFileSize = (file: ModelViewerFile) => {
  return _.get(file, "data.size", 0);
};

export type ModelViewerFile = {
  id: string;
  data: Partial<PfsFileData> | null;
  geometry?: Record<string, unknown> | null;
};

export type FormDataFile = {
  id: string;
  originalFilename: string;
  fileExtension: string;
};

const toModelViewerFile = (file: FormDataFile): ModelViewerFile => {
  const { id, ...rest } = file;
  return {
    id,
    data: { ...rest },
  };
};

const formatPartDimensions = (
  box: { x: number; y: number; z: number },
  boxUnits: UnitOfMeasurement
) => {
  const { x, y, z } = box;

  if (boxUnits === UnitOfMeasurement.Mm) {
    return `${x.toFixed(2)}mm x ${y.toFixed(2)}mm x ${z.toFixed(2)}mm`;
  }

  return `${x.toFixed(3)}in x ${y.toFixed(3)}in x ${z.toFixed(3)}in`;
};

const getEntitiesFromProposal = (
  proposal: MarketplaceProposal
): FileEntity[] => {
  const entities: FileEntity[] = [
    {
      type: FileShareEntityType.PROPOSAL,
      id: proposal.id,
    },
  ];
  const orderId = proposal.order?.id;
  if (orderId) {
    entities.push({
      type: FileShareEntityType.MARKETPLACE_ORDER,
      id: orderId,
    });
  }
  const { opportunityId } = proposal;
  if (opportunityId) {
    entities.push({
      type: FileShareEntityType.OPPORTUNITY,
      id: opportunityId,
    });
  }
  const invoiceId = proposal.invoice?.id;
  if (invoiceId) {
    entities.push({
      type: FileShareEntityType.INVOICE,
      id: invoiceId,
    });
  }
  return entities;
};

export default {
  buildDownloadUrl,
  getFileSize,
  getOriginalFilename,
  pfsUploadFiles,
  getFileExtension,
  getFileThumbnailUrl,
  getFileStreamcacheUrl,
  callWithRetry,
  shouldHaveStreamcacheFile,
  formatPartDimensions,
  fileNeedUserInputForUnits,
  shouldHaveThumbnailImage,
  toModelViewerFile,
  getEntitiesFromProposal,
  uploadToUrl,
};
